import { useState, useEffect, useContext } from 'react';
import { toast } from 'sonner';
import { Loader2, Mail } from 'lucide-react';
import { sendIssueNotification } from '@/lib/api';
import { Modal } from '@/components/ui/modal';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { AuthContext } from '@/lib/auth';
import { format } from 'date-fns';
import { supabase } from '@/lib/supabase';
import type { ReportedIssue, Recipient, NotificationLog } from '@/types';

interface SendNotificationDialogProps {
  issue: ReportedIssue;
  open: boolean;
  onClose: () => void;
}

export function SendNotificationDialog({ issue, open, onClose }: SendNotificationDialogProps) {
  const { session } = useContext(AuthContext);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [selectedRecipients, setSelectedRecipients] = useState<string[]>([]);
  const [notificationLogs, setNotificationLogs] = useState<NotificationLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [isRejected, setIsRejected] = useState(false);

  useEffect(() => {
    if (open) {
      fetchRecipients();
      fetchNotificationLogs();
      setSendError(null);
      setIsRejected(issue.status === 'rejected');
    }
  }, [open, issue.id]);

  useEffect(() => {
    // Reset selected recipients when dialog closes
    if (!open) {
      setSelectedRecipients([]);
    }
  }, [open]);

  const fetchRecipients = async () => {
    setIsLoading(true);
    try {
      // Get issue details with school information
      const { data: issueDetails, error: issueError } = await supabase
        .from('sms_reported_issues')
        .select(`
          school:sms_schools(
            region_id,
            school_level_id
          )
        `)
        .eq('id', issue.id)
        .single();

      if (issueError) throw issueError;

      // Fetch recipients that match the school's region and level
      const { data, error } = await supabase
        .from('sms_recipients')
        .select(`
          *,
          subscriptions:sms_recipient_region_school_levels!inner(
            id,
            region_id,
            school_level_id,
            region:sms_regions(*),
            school_level:sms_school_levels(*)
          )
        `)
        .eq('sms_recipient_region_school_levels.region_id', issueDetails.school.region_id)
        .eq('sms_recipient_region_school_levels.school_level_id', issueDetails.school.school_level_id);

      if (error) throw error;

      // Transform data to match expected format
      const transformedData = data?.map(recipient => ({
        ...recipient,
        subscriptions: recipient.subscriptions
      })) || [];

      setRecipients(transformedData);
    } catch (error) {
      console.error('Error fetching recipients:', error);
      toast.error('Failed to fetch recipients');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchNotificationLogs = async () => {
    try {
      const { data, error } = await supabase
        .from('sms_notification_logs')
        .select('*')
        .eq('issue_id', issue.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setNotificationLogs(data || []);
    } catch (error) {
      console.error('Error fetching notification logs:', error);
    }
  };

  const hasManagerPermission = session?.user?.role === 'app.manager';

  useEffect(() => {
    if (open && !hasManagerPermission) {
      toast.error('You need manager permissions to send notifications');
      onClose();
    }
  }, [open, hasManagerPermission, onClose]);

  const handleSend = async () => {
    if (!hasManagerPermission) {
      toast.error('You need manager permissions to send notifications');
      return;
    }

    if (!selectedRecipients.length) {
      toast.error('Please select at least one recipient');
      return;
    }

    setIsSending(true);
    setSendError(null);

    try {
      const data = await sendIssueNotification(
        issue.id,
        selectedRecipients
      );

      console.log('Notification response:', data);

      const { results } = data;
      if (results) {
        if (results.success > 0) {
          toast.success(`Successfully sent ${results.success} notification${results.success !== 1 ? 's' : ''}`);
        }
        if (results.failed > 0) {
          const errorMessage = results.errors?.length
            ? `\n\nErrors:\n${results.errors.join('\n')}`
            : '';
          toast.error(
            `Failed to send ${results.failed} notification${results.failed !== 1 ? 's' : ''}.${errorMessage}`,
            { duration: 5000 }
          );
        }
      } else {
        toast.success('Notifications processed successfully');
      }

      fetchNotificationLogs();
      onClose();
    } catch (error) {
      const isConnectivityError = error.message?.includes('Unable to connect');
      const isAuthError = error.message?.includes('No active session');
      
      if (isAuthError) {
        setSendError('Your session has expired. Please refresh the page and try again.');
        return;
      }

      toast.error(
        isConnectivityError
          ? 'Unable to reach notification service. Please check your connection.'
          : error.message,
        { duration: 5000 }
      );
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Modal
      title="Send Notifications"
      open={open}
      confirmDisabled={isSending || !selectedRecipients.length || !!sendError || isRejected}
      onClose={onClose}
      onConfirm={handleSend}
      confirmText={
        isRejected 
          ? 'Notifications Disabled' 
          : isSending 
            ? 'Sending...' 
            : 'Send Notifications'
      }
    >
      <div className="space-y-6">
        {isRejected && (
          <div className="bg-red-50 text-red-700 p-4 rounded-md text-sm">
            <p className="font-medium">Cannot Send Notifications</p>
            <p>This issue has been rejected and notifications cannot be sent.</p>
            {issue.remarks && (
              <div className="mt-2">
                <p className="font-medium">Rejection Reason:</p>
                <p className="whitespace-pre-wrap">{issue.remarks}</p>
              </div>
            )}
          </div>
        )}

        {sendError && (
          <div className="bg-red-50 text-red-700 p-4 rounded-md text-sm">
            {sendError}
          </div>
        )}

        {/* Recipients Selection */}
        <div>
          <h3 className="text-sm font-medium mb-3">Select Recipients</h3>
          {isLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : !isRejected ? (
            <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-2">
              {recipients.map((recipient) => (
                <div key={recipient.id} className="flex items-start gap-2 p-2 hover:bg-accent rounded-md">
                  <Checkbox
                    id={recipient.id}
                    checked={selectedRecipients.includes(recipient.id)}
                    onCheckedChange={(checked) => {
                      setSelectedRecipients(
                        checked
                          ? [...selectedRecipients, recipient.id]
                          : selectedRecipients.filter(id => id !== recipient.id)
                      );
                    }}
                  />
                  <div className="space-y-2 flex-1">
                    <label
                      htmlFor={recipient.id}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {recipient.name}
                    </label>
                    <p className="text-xs text-muted-foreground">{recipient.email}</p>
                    <div className="flex flex-wrap gap-1">
                      {recipient.subscriptions?.map((sub: any) => (
                        <Badge
                          key={`${sub.region.id}-${sub.school_level.id}`}
                          variant="outline"
                          className="bg-blue-50 text-blue-700 ring-blue-700/10 text-xs whitespace-nowrap"
                        >
                          {sub.region.name} - {sub.school_level.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4 text-sm text-muted-foreground">
              Notifications are disabled for rejected issues
            </div>
          )}
        </div>

        {/* Notification History */}
        {notificationLogs.length > 0 && (
          <div>
            <h3 className="text-sm font-medium mb-3">Notification History</h3>
            <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-2">
              {notificationLogs.map((log) => (
                <div key={log.id} className="flex items-center justify-between text-sm p-2">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">{log.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="outline"
                      className={
                        log.status === 'sent'
                          ? 'bg-green-50 text-green-700 ring-green-700/10'
                          : 'bg-red-50 text-red-700 ring-red-700/10'
                      }
                    >
                      {log.status}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {format(new Date(log.created_at), 'MMM d, HH:mm')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}